import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams, Link } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import InputField from "../../components/UI/Forms/InputField";
import ErrorMessage from "../../components/UI/Notifications/ErrorMessage";
import Button from "../../components/UI/Buttons/Button";
import ConfirmationModal from "../../components/UI/Modal/ConfirmationModal";
import SearchableMultiSelect, { OptionItem } from "../../components/UI/Forms/SearchableMultiSelect";
import useConfirmationModal from "../../hooks/useConfirmationModal";
import { callApi } from "../../api/callApi";
import { API_ENDPOINT, API_URL_2 } from "../../api/constant";
import { showToastNotification } from "../../utility/helper";
import { IModifierCollection, IModifierGroup } from "../../interfaces/modifier";
import moment from "../../utility/moment-timezone";

const ModifierCollectionForm: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
    } = useForm<any>({ mode: "onBlur", defaultValues: { sortOrder: 0 } });

    const [loadingButton, setLoadingButton] = useState<boolean>(false);
    const [existingCollection, setExistingCollection] = useState<IModifierCollection | null>(null);
    const [allGroups, setAllGroups] = useState<IModifierGroup[]>([]);
    const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
    const { showModal, modalProps, confirmAction } = useConfirmationModal();

    useEffect(() => {
        fetchAllGroups();
        if (id) fetchCollection(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchAllGroups = async () => {
        try {
            const res = await callApi(`${API_URL_2}${API_ENDPOINT.page}modifier?all=true`, { method: "GET" });
            const list: IModifierGroup[] = Array.isArray(res) ? res : res?.data || [];
            setAllGroups(list);
        } catch (error: any) {
            console.error("Failed to fetch modifier groups:", error.message);
        }
    };

    const fetchCollection = async (collectionId: string) => {
        try {
            const res: IModifierCollection = await callApi(
                `${API_URL_2}${API_ENDPOINT.page}modifier/main/${collectionId}`,
                { method: "GET" }
            );
            setExistingCollection(res);
            setValue("label", res.label);
            setValue("sortOrder", res.sortOrder ?? 0);

            // Pre-select assigned group IDs
            const assignedIds = res.modifierGroupIds || res.modifierGroups?.map((g) => g.id) || [];
            setSelectedGroupIds(assignedIds);
        } catch (error: any) {
            console.error("Failed to fetch modifier collection:", error.message);
            showToastNotification("danger", "Failed to load modifier collection");
        }
    };

    const onSubmit = async (data: any) => {
        setLoadingButton(true);
        const payload = {
            label: data.label,
            sortOrder: data.sortOrder ? Number(data.sortOrder) : 0,
            modifierGroupIds: selectedGroupIds,
        };
        try {
            if (id) {
                await callApi(`${API_URL_2}${API_ENDPOINT.page}modifier/main/${id}`, { method: "PATCH", data: payload });
                showToastNotification("success", "Modifier collection updated successfully");
                fetchCollection(id);
            } else {
                const res = await callApi(`${API_URL_2}${API_ENDPOINT.page}modifier/main`, { method: "POST", data: payload });
                showToastNotification("success", "Modifier collection created successfully");
                navigate(`/modifier-collections/edit/${res.id}`);
            }
        } catch (error: any) {
            showToastNotification("danger", error.message || "Failed to save modifier collection");
        } finally {
            setLoadingButton(false);
        }
    };

    /* ------------- Group removal from collection ------------- */
    const handleRemoveGroup = (groupId: string) => {
        confirmAction({
            title: "Remove Group",
            message: "Are you sure you want to remove this modifier group from the collection? The group itself will not be deleted.",
            onConfirm: async () => {
                const updatedIds = selectedGroupIds.filter((gId) => gId !== groupId);
                setSelectedGroupIds(updatedIds);
                if (id) {
                    try {
                        await callApi(`${API_URL_2}${API_ENDPOINT.page}modifier/main/${id}`, {
                            method: "PATCH",
                            data: {
                                label: existingCollection?.label,
                                sortOrder: existingCollection?.sortOrder ?? 0,
                                modifierGroupIds: updatedIds,
                            },
                        });
                        showToastNotification("success", "Modifier group removed from collection");
                        fetchCollection(id);
                    } catch (error: any) {
                        showToastNotification("danger", error.message || "Failed to remove group");
                        fetchCollection(id);
                    }
                }
            },
        });
    };

    const groupOptions: OptionItem[] = allGroups.map((g) => ({
        label: g.name,
        value: g.id,
        sublabel: `${g.options?.length ?? 0} Options`,
    }));

    // Filter assigned groups for nested table view
    const linkedGroups = allGroups.filter((g) => selectedGroupIds.includes(g.id));

    return (
        <Layout>
            {/* Page header with breadcrumb-style navigation */}
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
                <div>
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb mb-1">
                            <li className="breadcrumb-item">
                                <Link to="/modifier-collections">Modifier Collections</Link>
                            </li>
                            <li className="breadcrumb-item active" aria-current="page">
                                {id ? "Edit" : "Add"}
                            </li>
                        </ol>
                    </nav>
                    <h1 className="app-page-title mb-0">
                        {id ? "Edit Modifier Collection" : "Add Modifier Collection"}
                    </h1>
                </div>
                <button className="btn btn-outline-secondary text-nowrap" onClick={() => navigate("/modifier-collections")}>
                    <i className="bi bi-arrow-left me-2" /> Back to Collections
                </button>
            </div>

            {/* Basic Information */}
            <div className="app-card app-card-settings shadow-sm p-3 p-md-4 mb-4">
                <div className="app-card-body">
                    <h5 className="fw-bold mb-3">Basic Information</h5>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="row">
                            <div className="mb-3 col-md-6 col-12">
                                <InputField
                                    type="text"
                                    register={register("label", { required: "Label is required" })}
                                    name="label"
                                    label="Label"
                                    placeholder="Enter collection label"
                                />
                                <ErrorMessage message={errors.label?.message?.toString()} />
                            </div>
                            <div className="mb-3 col-sm-6 col-md-3 col-12">
                                <InputField
                                    type="number"
                                    register={register("sortOrder")}
                                    name="sortOrder"
                                    label="Sort Order"
                                    placeholder="0"
                                />
                            </div>
                            <div className="col-12 mb-3">
                                <SearchableMultiSelect
                                    label="Assign Modifier Groups"
                                    placeholder="Search & select modifier groups to assign..."
                                    options={groupOptions}
                                    selectedValues={selectedGroupIds}
                                    onChange={setSelectedGroupIds}
                                    helpText="Select one or multiple Modifier Groups to include in this collection."
                                />
                            </div>
                        </div>
                        <div className="col-12 mt-2 d-grid d-sm-flex justify-content-sm-end">
                            <Button
                                loading={loadingButton}
                                className="btn btn-primary text-white px-5"
                                type="submit"
                                text={id ? "Update" : "Save"}
                            />
                        </div>
                    </form>
                </div>
            </div>

            {/* Linked Modifier Groups summary table */}
            {id && (
                <div className="app-card app-card-settings shadow-sm p-3 p-md-4">
                    <div className="app-card-body">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                            <h5 className="fw-bold mb-0">Linked Modifier Groups ({linkedGroups.length})</h5>
                            <Link
                                to={`/modifiers/add?collectionId=${id}`}
                                className="btn app-btn-primary d-inline-flex align-items-center gap-1 text-nowrap btn-sm"
                            >
                                <i className="bi bi-plus-lg" /> Create New Modifier Group
                            </Link>
                        </div>

                        <div className="table-responsive">
                            <table className="table app-table-hover mb-0 text-left align-middle">
                                <thead>
                                    <tr>
                                        <th className="cell text-nowrap">Group Name</th>
                                        <th className="cell text-nowrap">Sort Order</th>
                                        <th className="cell text-nowrap">Options Count</th>
                                        <th className="cell text-nowrap">Status</th>
                                        <th className="cell text-nowrap">Created At</th>
                                        <th className="cell text-nowrap">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {linkedGroups.length === 0 ? (
                                        <tr>
                                            <td className="text-center" colSpan={6}>
                                                No modifier groups linked to this collection yet. Select groups above to link them.
                                            </td>
                                        </tr>
                                    ) : (
                                        linkedGroups.map((group) => (
                                            <tr key={group.id}>
                                                <td className="cell fw-semibold">{group.name}</td>
                                                <td className="cell">{group.sortOrder}</td>
                                                <td className="cell">{group.options?.length ?? 0}</td>
                                                <td className="cell">
                                                    <span className={`badge ${group.isActive ? "bg-success" : "bg-danger"}`}>
                                                        {group.isActive ? "Active" : "Inactive"}
                                                    </span>
                                                </td>
                                                <td className="cell text-nowrap">
                                                    {group.createdAt ? moment(group.createdAt).format("DD MMM, YYYY") : "-"}
                                                </td>
                                                <td className="cell">
                                                    <div className="d-flex align-items-center gap-1">
                                                        <button
                                                            type="button"
                                                            className="btn btn-success px-2 py-1 text-white"
                                                            onClick={() => navigate(`/modifiers/edit/${group.id}?collectionId=${id}`)}
                                                            title="Edit Group"
                                                        >
                                                            <i className="bi bi-pen" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-danger px-2 py-1 text-white"
                                                            onClick={() => handleRemoveGroup(group.id)}
                                                            title="Unlink Group"
                                                        >
                                                            <i className="bi bi-x-lg" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
            <ConfirmationModal {...modalProps} show={showModal} />
        </Layout>
    );
};

export default ModifierCollectionForm;
